import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Card,
  CardContent,
  Stack
} from "@mui/material";
import { Edit, Delete, Add, Save, Cancel } from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  getFacebookTemplates
} from "../api/axios";

export default function Templates() {
  const [templates, setTemplates]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [dialogMode, setDialogMode]   = useState("add");
  const [form, setForm]               = useState({ name: "", language: "" });
  const [fbTemplates, setFbTemplates] = useState([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  async function fetchTemplates() {
    setLoading(true);
    try {
      const res = await getTemplates();
      console.log("API templates:", res.data);
      setTemplates(res.data);
    } catch {
      toast.error("Failed to load templates.");
    } finally {
      setLoading(false);
    }
  }

  const handleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const openDialog = async (mode) => {
    setDialogMode(mode);
    if (mode === "add") {
      setLoading(true);
      try {
        const res = await getFacebookTemplates();
        setFbTemplates(res.data);
      } catch {
        toast.error("Failed to fetch Facebook templates.");
      } finally {
        setLoading(false);
      }
      setForm({ name: "", language: "" });
      setSelectedIds([]);
    } else if (selectedIds.length === 1) {
      const tpl = templates.find(t => t.id === selectedIds[0]);
      setForm({ name: tpl.name, language: tpl.language });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (dialogMode === "add") {
        await createTemplate(form);
        toast.success("Template added successfully.");
      } else {
        await updateTemplate(selectedIds[0], form);
        toast.success("Template updated successfully.");
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch {
      toast.error("Operation failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Delete ${selectedIds.length} template(s)?`)) return;
    setLoading(true);
    try {
      await Promise.all(selectedIds.map(id => deleteTemplate(id)));
      toast.success("Template(s) deleted.");
      setSelectedIds([]);
      fetchTemplates();
    } catch {
      toast.error("Delete failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 0, flexGrow: 1, mt: 0 }}>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      <Stack direction="row" spacing={2} mb={3}>
        <Button variant="contained" startIcon={<Add />} onClick={() => openDialog("add")}>
          Add
        </Button>
        <Button
          variant="outlined"
          startIcon={<Edit />}
          onClick={() => openDialog("update")}
          disabled={selectedIds.length !== 1}
        >
          Update
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<Delete />}
          onClick={handleDelete}
          disabled={!selectedIds.length}
        >
          Delete
        </Button>
      </Stack>

      {loading ? (
        <Box textAlign="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Card elevation={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>S.No</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell> {/* 👈 Added */}
                  <TableCell>Content</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((t, idx) => (
                  <TableRow key={t.id} hover selected={selectedIds.includes(t.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(t.id)}
                        onChange={() => handleSelect(t.id)}
                      />
                    </TableCell>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{t.name}</TableCell>
                    <TableCell>{t.language}</TableCell>
                    <TableCell>{t.type}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color:
                            t.status === "APPROVED"
                              ? "green"
                              : t.status === "REJECTED"
                              ? "red"
                              : "orange",
                        }}
                      >
                        {t.status || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                        {t.content}
                      </pre>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogMode === "add" ? "Add Template" : "Update Template"}</DialogTitle>
        <DialogContent>
          <CardContent sx={{ display: "grid", gap: 2 }}>
            {dialogMode === "add" ? (
              <TextField
                select
                label="Select Facebook Template"
                value={form.name}
                onChange={(e) => {
                  const sel = fbTemplates.find(t => t.name === e.target.value);
                  setForm({ name: sel.name, language: sel.language });
                }}
                fullWidth
              >
                {fbTemplates.map((t) => (
                  <MenuItem key={t.name} value={t.name}>
                    {t.name} ({t.language})
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <>
                <TextField
                  label="Name"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Language"
                  value={form.language}
                  onChange={(e) => setForm(f => ({ ...f, language: e.target.value }))}
                  fullWidth
                />
              </>
            )}
          </CardContent>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<Cancel />} onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" startIcon={<Save />} onClick={handleSubmit}>
            {dialogMode === "add" ? "Add" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
